import { Alert } from '@/components/ui/alert';
import { LoadingPage } from '@/components/ui/loading-page';
import { BillingApprovalDashboard } from '@/components/subscription/BillingApprovalDashboard';
import { axiosConfig } from '@/config/axios.config';
import { useAuth } from '@/contexts/auth-context';
import { useBillingContextQuery } from '@/hooks/queries/use-billing-context-query';
import { billingRedirectFor, needsBillingModal, readBillingContext } from '@/libs/billing-access';
import { serializePaywallUrl } from '@/libs/paywall-url-state';
import { usePaywall } from '@/hooks/use-paywall';
import { Button, Stack } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { decideFreePlanAccess } from './free-plan-access';

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

function ResolvedBillingModal({ onClosed }: { onClosed: () => void }) {
  const { paywall, closePaywall } = usePaywall();
  useEffect(() => {
    if (paywall === 'plans') closePaywall({ notify: false, history: 'replace' });
    onClosed();
  }, [paywall, closePaywall, onClosed]);
  return null;
}

/** Billing access comes from the organization reader; existing money keeps its own permissions. */
export default function SubscriptionGuard({ children }: SubscriptionGuardProps): React.ReactNode {
  const { t } = useTranslation();
  const { user, isLoading: isLoadingUser } = useAuth();
  const location = useLocation();
  const isExemptRoute =
    location.pathname === '/' || axiosConfig.shouldSkipPaywallRedirect(location.pathname);
  const currentBrandId = user?.currentBrandId;
  const organizationId = user?.currentBrand?.organizationId;
  const scopeKey = JSON.stringify([user?.id, organizationId, currentBrandId]);
  const shouldRead = !isExemptRoute && !isLoadingUser && !!currentBrandId;
  const context = useBillingContextQuery({ enabled: shouldRead });
  const mandatoryBilling =
    shouldRead &&
    !!user?.id &&
    !context.error &&
    needsBillingModal(context.data, { brandId: currentBrandId, organizationId });
  const requiredScope = useRef<string | null>(null);
  useEffect(() => {
    if (mandatoryBilling) requiredScope.current = scopeKey;
    else if (requiredScope.current !== scopeKey) requiredScope.current = null;
  }, [mandatoryBilling, scopeKey]);

  if (isExemptRoute) return children;
  if (isLoadingUser || (shouldRead && context.isPending)) return <LoadingPage />;
  if (!currentBrandId) return children;
  if (context.error || !context.data) {
    return (
      <Stack maxW="480px" mx="auto" px={5} py={10}>
        <Alert status="error" title={t('settings.subscription.accessReadError')} />
        <Button variant="outline" onClick={() => void context.refetch()}>
          {t('common.retry')}
        </Button>
      </Stack>
    );
  }
  if (context.data.brandId !== currentBrandId || context.data.organizationId !== organizationId)
    return <LoadingPage />;
  const { access, accessSource } = context.data;
  // A free organization still onboarding resumes its onboarding first, before
  // the card, the store or the Shopify approval its plan asks for: the sales
  // channel it picks there decides whether a card is needed at all (a brand
  // selling on Shopify connects its store instead). A lapsed subscription is not the free plan, so an
  // old free stamp never sends a paying merchant back to the onboarding.
  const freePlan = decideFreePlanAccess(user);
  const onTheFreePlan =
    accessSource === 'free_plan' ||
    access.reason === 'card_required' ||
    access.reason === 'shopify_store_required' ||
    access.reason === 'shopify_approval_required';
  if (onTheFreePlan && freePlan.onFreePlan && freePlan.redirectTo)
    return <Navigate to={freePlan.redirectTo} replace />;
  if (mandatoryBilling) {
    const destination = serializePaywallUrl(
      location.pathname === '/dashboard'
        ? location.pathname + location.search + location.hash
        : (billingRedirectFor(access, location.pathname + location.search + location.hash) ??
            '/dashboard'),
      { paywall: 'plans' }
    );
    if (
      location.pathname !== '/dashboard' ||
      new URLSearchParams(location.search).get('paywall') !== 'plans'
    )
      return <Navigate to={destination} replace />;
    return <BillingApprovalDashboard key={scopeKey} />;
  }
  if (!access.allowed) {
    const destination = billingRedirectFor(access, location.pathname + location.search);
    if (destination) return <Navigate to={destination} replace />;
    return null;
  }
  const canonical = readBillingContext(context.data);
  const completedMandatoryBilling =
    requiredScope.current === scopeKey &&
    !context.isFetching &&
    canonical?.access.allowed &&
    canonical.brandId === currentBrandId &&
    canonical.organizationId === organizationId;
  return (
    <>
      {completedMandatoryBilling && (
        <ResolvedBillingModal
          onClosed={() => {
            requiredScope.current = null;
          }}
        />
      )}
      {children}
    </>
  );
}
